import * as React from 'react';
import clsx from 'clsx';
import {
    getOmjsProjectInstance,
    getOperationData,
    getObjectsListData,
    getHints
} from 'selectors';

import { actions as appActions } from 'services/app/app-service';
import { actions as objectComparisonActions } from 'services/objects-comparison/objects-comparison-service';
import { actions as mapActions } from 'services/map/map-service';
import { actions as favoritesActions } from 'services/favorites/favorites-service';
import { actions as overlayWindowsActions } from 'services/overlay-windows/overlay-windows-service';
import { actions as containersActions } from 'services/containers/containers-service';

import IconZoomTo from 'static/images/icon-zoom-to.svg';
import IconFavourites from 'static/images/icon-favourites.svg';
import IconCompare from 'static/images/map-tools/icon-compare.svg';
import IconFilters from 'static/images/icon-filters.svg';
import IconExcel from 'static/images/icon-excell.svg';
import IconSortDirection from 'static/images/icon-sort-direction.svg';
import IconExternalLink from 'static/images/icon-extermal-link.svg';
import IconNrmList from 'static/images/icon-list-normal.svg';
import IconExtList from 'static/images/icon-list-ext.svg';
import {
    ComparableLayers,
    FolderCode,
    favouritesContainersCodes,
    BaseLayerCode
} from 'project-constants';
import { IObjectSignature } from 'types';
import { IState } from 'services/root-reducer';
import { useSelector } from 'react-redux';
import { routes } from 'routes';
import { AutoScrollbars, Button } from 'components';
import { HelpButtonWithPopover } from 'components/help-button';
import { useTranslation } from 'react-i18next';
import { isMobileOnlyFixed } from 'utils/is-fixed-ismobileonly';

export interface IObjectListProps {
    compareDisabled?: boolean;
    layerCode: string;
    onFilterClick: (() => void) | false;
    className?: string;
    smallFilterButton?: boolean;
}

const statSelector = (state: IState) => ({
    allLayersConfig: state.app.config.publication.layers_config,
    favorites: state.favorites.items
});

const compareDataSelector = (state: IState) => getOperationData(state, 'OBJECTS_COMPARISON');

export const ObjectsList = (props: IObjectListProps) => {
    const { layerCode } = props;
    const { allLayersConfig, favorites } = useSelector(statSelector);
    const project = useSelector(getOmjsProjectInstance);
    const compareData = useSelector(compareDataSelector);
    const data = useSelector((state: IState) => getObjectsListData(state, layerCode));
    const { t } = useTranslation();

    const autoScrollbarsRef = React.createRef<AutoScrollbars>();

    const PAGE_COUNT = isMobileOnlyFixed ? 10 : 12;
    const PAGE_SIZE = Math.ceil(data.allObjectsLength / PAGE_COUNT);
    const [ sort, setSort ] = React.useState([ '', false ]);
    const [ extendedList, setExtendedList ] = React.useState(false);
    const [ page, setPage ] = React.useState(0);

    React.useEffect(() => {
        if (routes.currRouteState['page']) {
            setPage(routes.currRouteState['page']);
        }
        document.title = `${data.containerName}. ${t('Ленинградская область')}`;
        return () => {};
    }, []);

    React.useEffect(() => {
        if (autoScrollbarsRef.current && routes.currRouteState.scrollTop) {
            autoScrollbarsRef.current.getScrollElement().scrollTop = routes.currRouteState.scrollTop;
        }
    }, [ autoScrollbarsRef.current ]);

    React.useMemo(() => {
        setPage(0);
    }, [ data.objects.length ]);

    const hints = useSelector(getHints);

    const contIds = data.containers.map(cont => cont.id);
    const layersConfig = allLayersConfig.reduce((acc, item) => {
        let index = contIds.indexOf(item.containerId);
        return index !== -1 ? { ...acc, [data.containers[index].code]: item } : acc;
    }, {});

    const createIconUrl = (cont, item) => {
        let iconUrl = cont.icon;

        if (cont.style_mode === 3) {
            const { columnCode, classesSettings } = cont.visualSettings;
            const itemClass = classesSettings.classes.find(cls => cls.value === item[columnCode]);
            iconUrl = itemClass.icon;
        }

        if (iconUrl && iconUrl.length) {
            if (iconUrl.charAt(0) !== '/') {
                iconUrl = `/${iconUrl}`;
            }
            let port = project.apiUrlLocation.port ? `:${project.apiUrlLocation.port}` : '';
            iconUrl = `${project.apiUrlLocation.protocol}//${project.apiUrlLocation.hostname}${port}${iconUrl}`;
        }

        return iconUrl;
    };

    const handleToggleFavorites = (evt: React.MouseEvent, layer_code: string, objId: number, active: boolean) => {
        evt.stopPropagation();
        if (active) {
            favoritesActions.removeItem({ object_id: objId, layer_code });
        } else {
            favoritesActions.addItem({ object_id: objId, layer_code });
        }
    };

    const handleToggleCompare = (evt: React.MouseEvent, layer_code: string, objId: number, active: boolean) => {
        evt.stopPropagation();
        if (active) {
            objectComparisonActions.removeItem({ layer_code, object_id: objId });
        } else {
            objectComparisonActions.addItem({ layer_code, object_id: objId });
        }
    };

    const handleFitToObject = (evt: React.MouseEvent, layer_code: string, objId: number) => {
        evt.stopPropagation();
        appActions.hideObjectFiltersWindow();
        mapActions.fitToTarget({ layer: layer_code, objectId: objId });
    };

    const handleItemClick = (layer_code: string, objId: number) => {
        routes.redirect(
            routes.object,
            { layerCode: layer_code, objectId: objId },
            true,
            { fromList: true, page: page, scrollTop: autoScrollbarsRef.current.getScrollElement().scrollTop }
        );
    };

    const handleSortList = (field) => {
        let newSort;
        if (field === 'favourites') {
            newSort = sort[0] === field ? [ '', false ] : [ field, false ];
        } else if (sort[0] === field) {
            newSort = sort[1] ? [ '', false ] : [ field, !sort[1] ];
        } else {
            newSort = [ field, false ];
        }
        setSort(newSort);
    };

    const getSortedArray = () => {
        let objects = [ ...data.objects ];
        let desc = sort[1] ? -1 : 1;
        if (sort[0] === 'name') {
            objects.sort((aObj, bObj) => {
                const aNameField = layersConfig[aObj.layerCode].objectNameColumnCode;
                const bNameField = layersConfig[bObj.layerCode].objectNameColumnCode;
                return String(aObj[aNameField]).localeCompare(String(bObj[bNameField])) * desc;
            });
        }

        if (sort[0] === 'favourites') {
            objects.sort((aObj, bObj) => {
                let aFav = aObj.layerCode in favorites && favorites[aObj.layerCode].includes(aObj.id) ? 0 : 1;
                let bFav = bObj.layerCode in favorites && favorites[bObj.layerCode].includes(bObj.id) ? 0 : 1;
                return (aFav - bFav) * desc;
            });
        }
        return objects;
    };

    const highlightMapFeature = (layer_code: string, objectId: number) => {
        mapActions.highlightObject({ layerCode: layer_code, objectId });
    };

    const exportList = () => {
        let { containers } = data;
        let objects = [ ...data.objects ];

        if (
            containers.length === 1
            || ((layerCode in BaseLayerCode) && layerCode !== 'programs')
        ) {
            let [ container ] = containers;
            let contCode = layerCode in BaseLayerCode
                ? BaseLayerCode[layerCode]
                : container.code;

            containersActions.exportObjects({
                container_code: contCode,
                object_ids: objects.map(o => o.id)
            });
        } else {
            let signatures: IObjectSignature[] = objects.map(
                o => ({ layer_code: o.layerCode, object_id: o.id })
            );
            overlayWindowsActions.showExportLayerChooserOverlay(signatures);
        }
    };

    const renderHelpButton = (containerCode: string) => {
        const hint = hints.find(obj => obj.get('code') === `${containerCode}_list`);
        if (hint) {
            const { title, text } = hint['_attributes'];
            return (
                <HelpButtonWithPopover
                    className="for-objects-list"
                    popoverProps={{
                        placement: 'bottom',
                        className: 'help-button-popover bottom-placement'
                    }}
                    offset={[ 0, 24 ]}
                >
                    <div className="header">
                        {title}
                    </div>
                    <div className="body">
                        {text}
                    </div>
                </HelpButtonWithPopover>
            );
        }

        return null;
    };

    const renderPaginator = () => {
        let pagesArrayLength = 0;
        for (;;) {
            let startIndex = PAGE_SIZE * pagesArrayLength;
            let endIndex = startIndex + PAGE_SIZE;
            let a = data.objects.slice(startIndex, endIndex);

            if (!a.length) {
                break;
            } else {
                pagesArrayLength++;
            }
        }

        let indexes = (new Array(pagesArrayLength))
            .fill(0)
            .map((val, i) => i);

        return (
            <div className="paginator">
                {indexes.map(val => (
                    <div
                        key={`${val}_ind`}
                        className={clsx('paginator-page', page === val && 'active')}
                        onClick={() => {
                            setPage(val);
                            autoScrollbarsRef.current.getScrollElement().scrollTop = 0;
                        }}
                    >
                        {val + 1}
                    </div>
                ))}
            </div>
        );
    };

    const renderList = () => {
        const containers = data.containers.reduce((acc, item) => ({ ...acc, [item.code]: item }), {});
        let objects = getSortedArray();

        if (layerCode === 'programs') {
            objects = objects.splice(PAGE_SIZE * page, PAGE_SIZE);
        }

        return (
            <AutoScrollbars ref={autoScrollbarsRef}>
                <div className="object-list__items-wrapper">
                    {objects.map((item) => {
                        const layerConf = layersConfig[item.layerCode];
                        let objectName = item[layerConf.objectNameColumnCode];
                        objectName = objectName?.replace(/&quot;/gm, '"');
                        let favFlag = item.layerCode in favorites && favorites[item.layerCode].includes(item.id);
                        let compareFlag = compareData.object_ids.find(obj => (
                            obj.layer_code === item.layerCode && obj.object_id === item.id
                        )) !== undefined;

                        return (
                            <div key={`${item.id}_${item.layerCode}`} className="object-list__item">
                                <div
                                    className="object-list__item-wrapper"
                                    key={item.id}
                                    onClick={() => handleItemClick(item.layerCode, item.id)}
                                    onMouseEnter={() => highlightMapFeature(item.layerCode, item.id)}
                                >
                                    <div className="object-list__item-icon">
                                        <img src={createIconUrl(containers[item.layerCode], item)} alt="" />
                                    </div>
                                    <div className="object-list__item-name">{objectName}</div>
                                    {!isMobileOnlyFixed && (
                                        <div
                                            className="object-list__item-zoom"
                                            onClick={evt => handleFitToObject(evt, item.layerCode, item.id)}
                                        >
                                            <IconZoomTo />
                                        </div>
                                    )}
                                    {
                                        !isMobileOnlyFixed
                                        && !props.compareDisabled
                                        && ComparableLayers.has(item.layerCode)
                                        && (
                                            <div
                                                className={clsx('object-list__item-compare', compareFlag && 'active')}
                                                onClick={evt => handleToggleCompare(
                                                    evt, item.layerCode, item.id, compareFlag
                                                )}
                                            >
                                                <IconCompare />
                                            </div>
                                        )
                                    }
                                    {(!isMobileOnlyFixed && favouritesContainersCodes.includes(layerCode)) && (
                                        <div
                                            className={clsx('object-list__item-favourites', favFlag && 'active')}
                                            onClick={evt => handleToggleFavorites(
                                                evt, item.layerCode, item.id, favFlag
                                            )}
                                        >
                                            <IconFavourites />
                                        </div>
                                    )}
                                </div>
                                {extendedList && (
                                    <div className="object-list__item-ext-info">
                                        <div className="ext-info-item">
                                            <span>
                                                {t('Собственник')}
                                            </span>
                                            <div dangerouslySetInnerHTML={{
                                                __html: item['name_owner_maps']
                                            }}
                                            />
                                        </div>
                                        <div className="ext-info-item">
                                            <span>
                                                {t('Контактное лицо')}
                                            </span>
                                            <div dangerouslySetInnerHTML={{
                                                __html: item['contact_person_maps']
                                            }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </AutoScrollbars>
        );
    };

    const renderFooter = () => {
        let footerElements = [];
        let objects = [ ...data.objects ];

        if (layerCode === FolderCode.TOSER) {
            footerElements.push(
                <a
                    href="http://lenoblinvest.ru/images/2018/PIkalevo_new.pdf"
                    className="external-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    key='toser-button-1'
                >
                    <IconExternalLink />
                    {t('Свободные площади для резидентов ТОСЭР')}
                </a>
            );

            footerElements.push(
                <a
                    href="http://lenoblinvest.ru/monogoroda-leningradskoj-oblasti/"
                    className="external-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    key='toser-button-2'
                >
                    <IconExternalLink />
                    {t('Общая информация')}
                </a>
            );
        }

        // if (layerCode === FolderCode.LANDS) {
        //     footerElements.push(
        //         <a
        //             href="https://map.lenoblinvest.ru/incentives/"
        //             className="external-link"
        //             target="_blank"
        //             rel="noopener noreferrer"
        //             key='lands-button-1'
        //         >
        //             <IconExternalLink />
        //             {t('Земельные участки в аренду')}
        //         </a>
        //     );
        // }

        if (!isMobileOnlyFixed) {
            let objectsCountLabel = (
                data.objects.length === data.unfilteredObjects.length
                    ? data.unfilteredObjects.length.toString()
                    : `${data.objects.length} / ${data.unfilteredObjects.length}`
            );

            footerElements.push(
                <Button
                    disabled={data.objects.length === 0}
                    text={`${t('Скачать список')} | ${objectsCountLabel}`}
                    icon={<IconExcel />}
                    onClick={exportList}
                    key='download-list-button'
                />
            );
        }

        if (footerElements.length === 0) {
            return null;
        } else {
            return (
                <div className="object-list__footer">
                    {footerElements}
                </div>
            );
        }
    };

    const Controls = () => (
        <div className="object-list__controls">
            <div className="object-list-sort">
                <div
                    onClick={() => handleSortList('name')}
                    className={clsx('list-sort__item', {
                        active: sort[0] === 'name',
                        desc: sort[0] === 'name' && sort[1] && 'desc'
                    })}
                >
                    {t('Название')}
                    <IconSortDirection />
                </div>
                {favouritesContainersCodes.includes(layerCode) && (
                    <div
                        onClick={() => handleSortList('favourites')}
                        className={clsx('list-sort__item', sort[0] === 'favourites' && 'active')}
                    >
                        <IconFavourites className="no-margin" />
                    </div>
                )}
            </div>
            {/* {isMobileOnlyFixed && layerCode === 'plots' && (
                <div
                    onClick={() => setExtendedList(!extendedList)}
                    className="object-list__ext-list-btn"
                >
                    {extendedList ? <IconExtList /> : <IconNrmList />}
                </div>
            )} */}
            {isMobileOnlyFixed && (
                <Button
                    disabled={data.objects.length === 0}
                    icon={<IconExcel />}
                    onClick={exportList}
                    style={{ marginRight: 10 }}
                />
            )}
            {(props.onFilterClick) && (
                <Button
                    text={!props.smallFilterButton ? t('Фильтры') : null}
                    icon={<IconFilters />}
                    onClick={props.onFilterClick}
                />
            )}
        </div>
    );

    return (
        <div className={clsx('object-list', props.className)}>
            <div className="object-list__header">
                {!isMobileOnlyFixed && (
                    <React.Fragment>
                        {data.containerName}
                        {renderHelpButton(layerCode)}
                        {layerCode === 'plots' && (
                            <div
                                onClick={() => setExtendedList(!extendedList)}
                                className="object-list__ext-list-btn"
                            >
                                {extendedList ? <IconExtList /> : <IconNrmList />}
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>
            { Controls() }
            {layerCode === 'programs' && renderPaginator()}
            <div className="object-list__items">
                { renderList() }
            </div>
            { renderFooter() }
        </div>
    );
};
